/**
 * Route predicate for the two onboarding wizards.
 *
 * Both wizards are full-screen, single-task surfaces rendered by
 * `SelfServiceLayout` rather than `DashboardLayout`:
 *
 *   - `/join/:orgId/:stepId`        — the invited ("nth user") flow
 *   - `/get-started/setup/:stepId`  — the workspace-creator flow
 *
 * Global chrome mounted ABOVE the router (`main.tsx`'s `<Toaster>`, and the
 * proposal websocket living in `TeamProvider`) cannot see which layout is
 * rendered, so route-scoped suppression needs a pathname test. This module is
 * that test, kept separate so a consumer like `lib/proposals` depends on one
 * neutral predicate rather than reaching into either wizard's constants.
 *
 * Shaped after `lib/accessPaused.ts` / `lib/billingRequired.ts`, the existing
 * path-predicate modules.
 */
import { JOIN_BASE_PATH } from '@/lib/join-flow/constants';
import { GET_STARTED_PATH } from '@/lib/self-service/constants';

/**
 * Matches `/join/<orgId>` and anything beneath it. The org segment is required
 * and numeric so an unrelated future `/joinery` route can never match.
 */
const JOIN_FLOW_PATH_RE = new RegExp(`^${JOIN_BASE_PATH}/\\d+(/|$)`);

/** Matches `/get-started` and anything beneath it, including `/get-started/setup/*`. */
const GET_STARTED_PATH_RE = new RegExp(`^${GET_STARTED_PATH}(/|$)`);

/**
 * True when `pathname` is inside either onboarding wizard.
 *
 * Used to suppress interruptions that are correct on the dashboard but wrong
 * mid-onboarding — the new-proposal toast in particular, whose card navigates
 * with `window.location.assign` and would therefore drop a half-finished
 * invitee out of the wizard entirely.
 */
export function isOnboardingFlowPath(pathname: string): boolean {
  return JOIN_FLOW_PATH_RE.test(pathname) || GET_STARTED_PATH_RE.test(pathname);
}
